import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, Plus, RotateCcw, Share2, Trash2, TrendingUp } from "lucide-react";
import {
  americanError,
  computeParlay,
  formatAmerican,
  formatDecimal,
  formatPercent,
  formatUSD,
} from "@/lib/oddsMath";
import { PARLAY } from "@/constants/testIds";
import { EVENTS, track } from "@/lib/analytics";

const MAX_LEGS = 15;
const MIN_LEGS = 2;

const EXAMPLES = [
  {
    label: "$25 · 3-leg (-110, -110, +150)",
    bet: "25",
    legs: ["-110", "-110", "+150"],
  },
  {
    label: "$50 · 2-leg (+120, +120)",
    bet: "50",
    legs: ["+120", "+120"],
  },
  {
    label: "$10 · 5-leg (-110, -105, +100, +130, -120)",
    bet: "10",
    legs: ["-110", "-105", "+100", "+130", "-120"],
  },
];

const initialLegs = () => ["", ""];

const betError = (raw) => {
  if (raw === "" || raw === null || raw === undefined) return "";
  const trimmed = String(raw).trim();
  if (!/^\d*\.?\d*$/.test(trimmed)) return "Enter a positive dollar amount.";
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return "Enter a valid amount.";
  if (n < 0) return "Bet amount cannot be negative.";
  return "";
};

export const ParlayCalculator = () => {
  const [bet, setBet] = useState("");
  const [legs, setLegs] = useState(initialLegs);

  const betErr = betError(bet);
  const legErrors = legs.map((l) => americanError(l));
  // Only surface errors for fields the user has interacted with (non-empty).
  const visibleBetErr = bet.trim() === "" ? "" : betErr;
  const visibleLegErrors = legs.map((l, i) =>
    String(l).trim() === "" ? "" : legErrors[i],
  );

  const cleanedLegs = useMemo(
    () => legs.map((l) => Number(String(l).trim())),
    [legs],
  );

  const canCompute =
    !betErr &&
    bet !== "" &&
    Number(bet) > 0 &&
    legs.length >= MIN_LEGS &&
    legErrors.every((e) => e === "");

  const result = useMemo(() => {
    if (!canCompute) return null;
    return computeParlay(Number(bet), cleanedLegs);
  }, [canCompute, bet, cleanedLegs]);

  // Fire parlay_calculated once per stable valid result (debounced to avoid
  // spamming an event on every keystroke). We only send non-PII metadata.
  const parlayFireTimer = useRef(null);
  useEffect(() => {
    if (parlayFireTimer.current) clearTimeout(parlayFireTimer.current);
    if (!result) return undefined;
    parlayFireTimer.current = setTimeout(() => {
      track(EVENTS.PARLAY_CALCULATED, {
        legs_count: cleanedLegs.length,
      });
    }, 600);
    return () => {
      if (parlayFireTimer.current) clearTimeout(parlayFireTimer.current);
    };
  }, [result, cleanedLegs.length]);

  const updateLeg = (index, value) => {
    setLegs((prev) => prev.map((l, i) => (i === index ? value : l)));
  };

  const addLeg = () => {
    if (legs.length >= MAX_LEGS) {
      toast.info(`Maximum ${MAX_LEGS} legs reached.`);
      return;
    }
    const next = [...legs, ""];
    setLegs(next);
    track(EVENTS.PARLAY_LEG_ADDED, { legs_count: next.length });
  };

  const removeLeg = (index) => {
    if (legs.length <= MIN_LEGS) {
      toast.info(`A parlay needs at least ${MIN_LEGS} legs.`);
      return;
    }
    const next = legs.filter((_, i) => i !== index);
    setLegs(next);
    track(EVENTS.PARLAY_LEG_REMOVED, { legs_count: next.length });
  };

  const reset = () => {
    setBet("");
    setLegs(initialLegs());
    toast.success("Calculator reset.");
  };

  const applyExample = (ex) => {
    setBet(ex.bet);
    setLegs(ex.legs);
  };

  const resultsText = () => {
    if (!result) return "";
    return [
      `Parlay Assistant — Parlay Payout`,
      `Bet: ${formatUSD(result.bet)}`,
      `Legs: ${cleanedLegs.map((l) => formatAmerican(l)).join(", ")}`,
      `Combined American Odds: ${formatAmerican(result.combinedAmerican)}`,
      `Combined Decimal Odds: ${formatDecimal(result.combinedDecimal)}`,
      `Implied Probability: ${formatPercent(result.impliedProbability)}`,
      `Potential Profit: ${formatUSD(result.profit)}`,
      `Total Payout: ${formatUSD(result.totalPayout)}`,
    ].join("\n");
  };

  const copyResults = async () => {
    if (!result) {
      toast.error("Enter a bet amount and valid odds first.");
      return;
    }
    try {
      await navigator.clipboard.writeText(resultsText());
      track(EVENTS.RESULTS_COPIED, {
        tool: "parlay",
        legs_count: cleanedLegs.length,
      });
      toast.success("Results copied to clipboard.");
    } catch {
      toast.error("Copy failed. Please try again.");
    }
  };

  const shareResults = async () => {
    if (!result) {
      toast.error("Enter a bet amount and valid odds first.");
      return;
    }
    const text = resultsText();
    track(EVENTS.RESULTS_SHARED, {
      tool: "parlay",
      legs_count: cleanedLegs.length,
      channel: navigator.share ? "web-share" : "clipboard",
    });
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Parlay Assistant — Payout",
          text,
          url: typeof window !== "undefined" ? window.location.href : undefined,
        });
      } catch {
        /* user canceled — no toast needed */
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Sharing not supported here. Results copied instead.");
    } catch {
      toast.error("Sharing not available in this browser.");
    }
  };

  return (
    <section id="parlay-calculator" data-testid={PARLAY.card} className="pa-card p-6 md:p-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <p className="font-display text-emerald-700 text-xs tracking-[0.2em] uppercase">
            Tool 01
          </p>
          <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tight text-slate-900 mt-1">
            Parlay Payout Calculator
          </h2>
          <p className="text-slate-600 mt-2 text-sm md:text-base max-w-xl">
            Enter your wager and the odds for each leg to calculate your potential parlay payout.
          </p>
        </div>
        <div className="flex items-center gap-2 text-slate-500 text-xs md:text-sm font-mono-num">
          <TrendingUp size={16} />
          <span>
            {legs.length} / {MAX_LEGS} legs
          </span>
        </div>
      </div>

      {/* Bet + Legs */}
      <div className="mt-7 grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-4">
          <label
            htmlFor="parlay-bet"
            className="block text-xs font-semibold uppercase tracking-wider text-slate-600"
          >
            Bet Amount (USD)
          </label>
          <div className="mt-2 relative">
            <span
              aria-hidden="true"
              className="absolute left-3.5 top-1/2 -translate-y-1/2 font-display text-slate-500 text-lg pointer-events-none select-none"
            >
              $
            </span>
            <input
              id="parlay-bet"
              data-testid={PARLAY.betInput}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              placeholder="0.00"
              value={bet}
              onChange={(e) => setBet(e.target.value)}
              data-invalid={visibleBetErr ? "true" : "false"}
              aria-invalid={!!visibleBetErr}
              aria-describedby={visibleBetErr ? "parlay-bet-error" : undefined}
              className="pa-input font-mono-num text-lg"
              style={{ paddingLeft: "2.25rem" }}
            />
          </div>
          {visibleBetErr && (
            <p
              id="parlay-bet-error"
              data-testid={PARLAY.betError}
              className="pa-inline-error"
            >
              {visibleBetErr}
            </p>
          )}

          <div className="mt-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
              Quick Examples
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {EXAMPLES.map((ex, i) => (
                <button
                  key={ex.label}
                  type="button"
                  data-testid={`${PARLAY.exampleBtn}-${i}`}
                  className="pa-chip"
                  onClick={() => applyExample(ex)}
                >
                  {ex.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="md:col-span-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
              Legs · American Odds
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={addLeg}
                disabled={legs.length >= MAX_LEGS}
                data-testid={PARLAY.addLeg}
                className="pa-btn pa-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus size={16} /> Add Leg
              </button>
              <button
                type="button"
                onClick={reset}
                data-testid={PARLAY.reset}
                className="pa-btn pa-btn-ghost"
                aria-label="Reset calculator"
              >
                <RotateCcw size={16} /> Reset
              </button>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {legs.map((leg, i) => {
              const err = visibleLegErrors[i];
              return (
                <div key={i} data-testid={PARLAY.leg(i)}>
                  <label
                    htmlFor={`parlay-leg-${i}`}
                    className="block text-xs font-medium text-slate-600"
                  >
                    Leg {i + 1}
                  </label>
                  <div className="mt-1 flex items-stretch gap-2">
                    <input
                      id={`parlay-leg-${i}`}
                      data-testid={PARLAY.legInput(i)}
                      value={leg}
                      onChange={(e) => updateLeg(i, e.target.value)}
                      placeholder={i === 0 ? "-110" : i === 1 ? "+150" : "e.g. -120"}
                      type="text"
                      inputMode="text"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      data-invalid={err ? "true" : "false"}
                      aria-invalid={!!err}
                      className="pa-input font-mono-num"
                    />
                    <button
                      type="button"
                      onClick={() => removeLeg(i)}
                      disabled={legs.length <= MIN_LEGS}
                      data-testid={PARLAY.legRemove(i)}
                      aria-label={`Remove leg ${i + 1}`}
                      className="pa-btn pa-btn-ghost !px-3 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  {err && (
                    <p
                      data-testid={PARLAY.legError(i)}
                      className="pa-inline-error"
                    >
                      {err}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="mt-8">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl uppercase tracking-wide text-slate-900">
            Results
          </h3>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={copyResults}
              data-testid={PARLAY.copyResults}
              className="pa-btn pa-btn-ghost !py-2 !text-xs"
            >
              <Copy size={14} /> Copy
            </button>
            <button
              type="button"
              onClick={shareResults}
              data-testid={PARLAY.shareResults}
              className="pa-btn pa-btn-ghost !py-2 !text-xs"
            >
              <Share2 size={14} /> Share
            </button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="pa-result">
            <span className="pa-result-label">Combined American</span>
            <span
              className="pa-result-value font-mono-num"
              data-testid={PARLAY.resultCombinedAmerican}
            >
              {result ? formatAmerican(result.combinedAmerican) : "—"}
            </span>
          </div>
          <div className="pa-result">
            <span className="pa-result-label">Combined Decimal</span>
            <span
              className="pa-result-value font-mono-num"
              data-testid={PARLAY.resultCombinedDecimal}
            >
              {result ? formatDecimal(result.combinedDecimal) : "—"}
            </span>
          </div>
          <div className="pa-result">
            <span className="pa-result-label">Implied Probability</span>
            <span
              className="pa-result-value font-mono-num"
              data-testid={PARLAY.resultImplied}
            >
              {result ? formatPercent(result.impliedProbability) : "—"}
            </span>
          </div>
          <div className="pa-result">
            <span className="pa-result-label">Potential Profit</span>
            <span
              className="pa-result-value font-mono-num"
              data-testid={PARLAY.resultProfit}
              style={{ color: result ? "#047857" : undefined }}
            >
              {result ? formatUSD(result.profit) : "—"}
            </span>
          </div>
          <div className="pa-result pa-result-emerald col-span-2 md:col-span-2">
            <span className="pa-result-label">Total Payout</span>
            <span
              className="pa-result-value font-mono-num"
              data-testid={PARLAY.resultPayout}
              style={{ fontSize: "2.4rem" }}
            >
              {result ? formatUSD(result.totalPayout) : "—"}
            </span>
          </div>
        </div>

        {!result && (
          <p className="mt-4 text-sm text-slate-500">
            Enter a positive bet amount and valid American odds for each leg to see your projected
            payout.
          </p>
        )}
      </div>
    </section>
  );
};

export default ParlayCalculator;
